import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTitleAndUser } from '../../../../../hooks/userTitle';
import {
  ArrowsDownUp,
  ArrowUp,
  ArrowDown,
  Buildings, 
  Info
} from '@phosphor-icons/react';
import { SideModal } from '../../../../../components/SideModal';
import {
  ContentList,
  RowDiv,
  WarningNewMessage,
} from '../../acoesPainelStyles';
import AsideFiltersEtfs from './AsideFiltersEtfs';
import api from '../../../../../services/api';
import { financeRoutes } from '../../../../../utils/RoutesApi';
import { CardEtfRank } from '../Internacionais/GraphicEtfs.jsx';

const PainelContainer = styled.div`
  width: 100%;
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 10px;
`;

const ContentPlaceholder = styled.div`
  width: 100%;
  padding: 1rem 0;
  margin-top: 3.2rem;
`;

const EtfsList = styled.div`
  width: 100%;
  border: 1px solid #d1d8e0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`;

const EtfsHeader = styled.div`
  display: grid;
  grid-template-columns: 2.2fr 1fr 1fr 1fr 1.4fr;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #d1d8e0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #111827;
  text-align: center;

  button {
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }

  button:hover {
    color: #ec6643;
  }
`;

export default function EtfsPainel({
  tipo = 'etf',
  isComponent = false,
  ticketActive = '',
  selectedActive = ({ slug, type }) => {},
}) {
  const { setTitle } = useTitleAndUser();
  const [modalFilter, setModalFilter] = useState(false);

  const [searchFilters, setSearchFilters] = useState({
    nome_ativo: '',
    tipo_ativo: tipo,
    currency: '',
    accounting_standard: '',
    bolsa: '',
    isFiltered: false,
  });

  const [etfs, setEtfs] = useState([]);
  const [loadingEtfs, setLoadingEtfs] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'ticker',
    direction: 'asc',
  });

  const filteredEtfs = [...etfs].sort((a, b) => {
    // Ordem alfabética por ticker (padrão); demais colunas são numéricas.
    if (sortConfig.key === 'ticker') {
      const cmp = (a.ticker || '').localeCompare(b.ticker || '');
      return sortConfig.direction === 'desc' ? -cmp : cmp;
    }

    const direction = sortConfig.direction === 'desc' ? 1 : -1;

    const sortMap = {
      dividendYield: getDividendYield,
      marketCap: getMarketCap,
      variation12m: getVariation12m,
      holdings: getHoldingsCount,
    };

    const getValue = sortMap[sortConfig.key];

    if (!getValue) return 0;

    return (getValue(b) - getValue(a)) * direction;
  });

  function getDividendYield(etf) {
    return Number(
      etf.latest_kpi?.dy ??
        etf.latest_kpi?.avg_dy ??
        etf.dividend_yield ??
        0
    );
  }

  function getVariation12m(etf) {
    return Number(etf.variation_12m ?? 0);
  }

  function getHoldingsCount(etf) {
    return Number(etf.holdings_count ?? etf.holdings?.length ?? 0);
  }

  function renderSortIcon(key) {
    if (sortConfig.key !== key) {
      return <ArrowsDownUp size={14} color="#94a3b8" />;
    }

    return sortConfig.direction === 'desc' ? (
      <ArrowDown size={14} color="#64748b" weight="bold" />
    ) : (
      <ArrowUp size={14} color="#64748b" weight="bold" />
    );
  }

  function handleSort(key) {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }

  // As métricas da listagem (DY, capitalização, variação 12M e holdings) já
  // vêm pré-calculadas do backend (EtfMetric). O detalhe completo de cada ETF
  // só é carregado ao clicar no ativo — não há mais fetch por ativo aqui.

  function getMarketCap(etf) {
    return Number(
      etf.latest_kpi?.market_cap ??
      etf.market_cap ??
      etf.capitalization ??
      0
    );
  }

  useEffect(() => {
    async function loadEtfs() {
      try {
        setLoadingEtfs(true);

        const response = await api.get(financeRoutes.INTERNATIONAL_ASSETS, {
          params: {
            asset_type: 'ETF',
            is_active: true,
            search: searchFilters.nome_ativo || undefined,
            exchange: searchFilters.bolsa || undefined,
            page: 1,
            page_size: 21,
          },
        });

        const etfsList = Array.isArray(response.data)
          ? response.data
          : response.data?.data || response.data?.results || [];

        const totalCount = response.data?.count || etfsList.length;
        const current = response.data?.current || 1;

        setCurrentPage(current);
        setNextPageUrl(etfsList.length < totalCount ? current + 1 : null);

        const rankedEtfs = etfsList
          .map((etf) => ({
            ...etf,
            score: calculateEtfScore(etf),
          }))
          .sort((a, b) => (a.ticker || '').localeCompare(b.ticker || ''));

        setEtfs(rankedEtfs);
      } catch (error) {
        console.error('Erro ao buscar ETFs internacionais:', error);
        setEtfs([]);
      } finally {
        setLoadingEtfs(false);
      }
    }

    if (tipo === 'etf') {
      loadEtfs();
    }
  }, [tipo, searchFilters.nome_ativo, searchFilters.bolsa]);

  useEffect(() => {
    function handleScroll() {
      const scrollPosition = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.offsetHeight;

      if (scrollPosition >= pageHeight - 300) {
        loadMoreEtfs();
      }
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [etfs, loadingMore, nextPageUrl, searchFilters.nome_ativo, searchFilters.bolsa]);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!isComponent) {
      setTitle('ETFs');
    }
  }, [isComponent, setTitle]);

  useEffect(() => {
    setSearchFilters((prev) => ({
      ...prev,
      tipo_ativo: tipo,
    }));
  }, [tipo]);

  function calculateEtfScore(etf) {
    const dividendYield = Number(
      etf.latest_kpi?.dy ??
        etf.latest_kpi?.avg_dy ??
        etf.dividend_yield ??
        0
    );

    // const variation12m = Number(etf.variation_12m ?? 0);

    const holdingsCount = Number(
      etf.holdings_count ??
        etf.holdings?.length ??
        0
    );

    const dividendYieldScore = Math.min(Math.max(dividendYield, 0), 20) * 4;
    // const variationScore = Math.min(Math.max(variation12m, 0), 100);
    const holdingsScore = Math.min(holdingsCount, 100);

    const penaltyWithoutHoldings = holdingsCount > 0 ? 1 : 0.45;
    const penaltyWithoutDividend = dividendYield > 0 ? 1 : 0.7;

    return (
      dividendYieldScore * 0.4 +
      // variationScore * 0.35 +
      holdingsScore * 0.25
    ) * penaltyWithoutHoldings * penaltyWithoutDividend;
  }

  async function loadMoreEtfs() {
    if (loadingMore) return;
    if (!nextPageUrl) return;

    try {
      setLoadingMore(true);

      const nextPage =
        typeof nextPageUrl === 'number' ? nextPageUrl : currentPage + 1;

      const response = await api.get(financeRoutes.INTERNATIONAL_ASSETS, {
        params: {
          asset_type: 'etf',
          is_active: true,
          search: searchFilters.nome_ativo || undefined,
          exchange: searchFilters.bolsa || undefined,
          page: nextPage,
          page_size: 21,
        },
      });

      const nextEtfs = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.results || [];

      const totalLoaded = etfs.length + nextEtfs.length;
      const totalCount = response.data?.count || totalLoaded;
      const current = response.data?.current || nextPage;

      setCurrentPage(current);
      setNextPageUrl(totalLoaded < totalCount ? current + 1 : null);

      const rankedNextEtfs = nextEtfs.map((etf) => ({
        ...etf,
        score: calculateEtfScore(etf),
      }));

      setEtfs((prev) =>
        [...prev, ...rankedNextEtfs].sort((a, b) =>
          (a.ticker || '').localeCompare(b.ticker || '')
        )
      );
    } finally {
      setLoadingMore(false);
    }
  }

  function handleChangeSearch(event) {
    const value = event.target.value;

    setSearchFilters((prev) => ({
      ...prev,
      nome_ativo: value,
      isFiltered: value.length > 0,
    }));
  }

  function renderTabTitle() {
    return 'ETFs';
  }

  function renderContentPlaceholder() {
    if (loadingEtfs) {
      return <ContentPlaceholder>Carregando ETFs...</ContentPlaceholder>;
    }

    if (!filteredEtfs.length) {
      return <ContentPlaceholder>Nenhum ETF encontrado.</ContentPlaceholder>;
    }

    return (
      <>
        <RowDiv>
          <EtfsList>
            <EtfsHeader>
              <button type="button" onClick={() => handleSort('ticker')}>
                Ativos {sortConfig.key === 'ticker' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
              </button>

              <button type="button" onClick={() => handleSort('dividendYield')}>
                Dividend Yield {renderSortIcon('dividendYield')}
              </button>

              <button type="button" onClick={() => handleSort('marketCap')}>
                Capitalização {renderSortIcon('marketCap')}
              </button>

              <button type="button" onClick={() => handleSort('variation12m')}>
                Variação 12M {renderSortIcon('variation12m')}
              </button>

              <button type="button" onClick={() => handleSort('holdings')}>
                Participações {renderSortIcon('holdings')}
              </button>
            </EtfsHeader>

            {filteredEtfs.map((etf) => (
              <CardEtfRank
                key={etf.id || etf.ticker}
                etf={etf}
                selectedActive={({ slug }) =>
                  selectedActive({
                    slug,
                    type: 'etf',
                  })
                }
              />
            ))}
          </EtfsList>
        </RowDiv>

        {loadingMore && <ContentPlaceholder>Carregando mais ETFs...</ContentPlaceholder>}
      </>
    );
  }

  if (tipo !== 'etf') {
    return null;
  }

  return (
    <div style={{ marginTop: '0%', background: 'transparent' }}>
      <ContentList>
        <div className="backPage" style={{ zIndex: '1' }}>
          <PainelContainer>
            {!isComponent && (
              <>
                <AsideFiltersEtfs
                  setSearchFilters={setSearchFilters}
                  searchFilters={searchFilters}
                  onChangeSearch={handleChangeSearch}
                  searchPlaceholder={`Buscar em ${renderTabTitle()}`}
                  showMobileButton={window.screen.width < 1100}
                  onOpenMobileFilters={() => setModalFilter(true)}
                />

                <br />

                <WarningNewMessage>
                  <Info color="#EC6643" size={24} />
                  <p>
                    O Ranking tem como objetivo orientar você a iniciar seus
                    estudos pelos ativos mais relevantes. No entanto, ele não
                    deve ser interpretado como uma recomendação específica de
                    compra ou venda.
                  </p>
                </WarningNewMessage>
              </>
            )}

            {renderContentPlaceholder()}
          </PainelContainer>
        </div>
      </ContentList>

      <SideModal
        id="sideFilterInternational"
        isOpen={modalFilter}
        setState={setModalFilter}
      >
        <AsideFiltersEtfs
          setSearchFilters={setSearchFilters}
          searchFilters={searchFilters}
          onChangeSearch={handleChangeSearch}
          searchPlaceholder={`Buscar em ${renderTabTitle()}`}
          showMobileButton={false}
          onOpenMobileFilters={() => setModalFilter(true)}
        />
      </SideModal>
    </div>
  );
}